import React, { useState } from 'react';
import { Gift, X, ArrowRight } from 'lucide-react';

const MOTHERS_DAY = { 2026: '2026-03-15', 2027: '2027-03-07', 2028: '2028-03-26', 2029: '2029-03-11', 2030: '2030-03-31' };

const thirdSundayOfJune = (year) => {
  const d = new Date(year, 5, 1);
  const firstSunday = 1 + ((7 - d.getDay()) % 7);
  return new Date(year, 5, firstSunday + 14);
};

const OCCASIONS = {
  valentines: {
    target: (y) => new Date(y, 1, 14),
    leadDays: 21,
    emoji: '💝',
    title: "Valentine's Day is coming!",
    message: 'Give the gift of relaxation — digital massage vouchers delivered instantly by email.',
    classes: 'bg-luxury-green-800 border-b border-luxury-gold/40',
  },
  mothersday: {
    target: (y) => (MOTHERS_DAY[y] ? new Date(MOTHERS_DAY[y] + 'T00:00:00') : null),
    leadDays: 21,
    emoji: '🌷',
    title: "Mother's Day is almost here!",
    message: 'Treat Mum to a massage gift voucher — the gift she actually wants.',
    classes: 'bg-luxury-green-800 border-b border-luxury-gold/40',
  },
  fathersday: {
    target: (y) => thirdSundayOfJune(y),
    leadDays: 21,
    emoji: '🎁',
    title: "Father's Day is coming up!",
    message: 'Give Dad a break — deep tissue or sports massage gift vouchers from €25.',
    classes: 'bg-luxury-green-700 border-b border-luxury-gold/40',
  },
  christmas: {
    target: (y) => new Date(y, 11, 25),
    leadDays: 28,
    emoji: '🎄',
    title: 'Christmas gifting made easy!',
    message: 'Instant digital massage vouchers — no wrapping needed, delivered by email.',
    classes: 'bg-luxury-green-900 border-b border-luxury-gold/40',
  },
};

export const getActiveOccasion = (now = new Date()) => {
  const preview = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('seasonal_preview');
  if (preview && OCCASIONS[preview]) return preview;
  for (const [key, occ] of Object.entries(OCCASIONS)) {
    const target = occ.target(now.getFullYear());
    if (!target) continue;
    const daysUntil = (target - now) / 86400000;
    if (daysUntil >= 0 && daysUntil <= occ.leadDays) return key;
  }
  return null;
};

const SeasonalVoucherBanner = () => {
  const activeKey = getActiveOccasion();
  const [dismissed, setDismissed] = useState(
    () => activeKey && sessionStorage.getItem(`seasonal-banner-dismissed-${activeKey}`) === '1'
  );

  if (!activeKey || dismissed) return null;
  const occ = OCCASIONS[activeKey];

  const handleDismiss = () => {
    sessionStorage.setItem(`seasonal-banner-dismissed-${activeKey}`, '1');
    setDismissed(true);
  };

  const scrollToVouchers = () => {
    document.getElementById('gift-vouchers')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={`${occ.classes} text-white relative`} data-testid="seasonal-voucher-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center sm:text-left">
        <p className="text-sm sm:text-base">
          <span className="mr-1">{occ.emoji}</span>
          <strong className="font-semibold">{occ.title}</strong>
          <span className="hidden md:inline font-light ml-2 opacity-90">{occ.message}</span>
        </p>
        <button
          onClick={scrollToVouchers}
          className="inline-flex items-center gap-1.5 bg-luxury-gold hover:bg-luxury-gold-600 text-luxury-green-900 rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold transition-colors flex-shrink-0"
          data-testid="seasonal-banner-cta"
        >
          <Gift size={14} />
          Buy Gift Voucher
          <ArrowRight size={14} />
        </button>
      </div>
      <button
        onClick={handleDismiss}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors"
        data-testid="seasonal-banner-dismiss"
        aria-label="Dismiss banner"
      >
        <X size={18} />
      </button>
    </div>
  );
};

export default SeasonalVoucherBanner;
